import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom"

import { ErrorBoundary } from "@/components/error-boundary"
import { Layout } from "@/components/layout"
import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "@/components/ui/sonner"
import { LocaleProvider, useT } from "@/i18n/provider"
import { AuthProvider, useAuth } from "@/lib/auth"
import { AcceptInvitePage } from "@/pages/accept-invite"
import { ActivityPage } from "@/pages/activity"
import { AdminAuditPage } from "@/pages/admin-audit"
import { AdminDebtsPage } from "@/pages/admin-debts"
import { AdminExpensesPage } from "@/pages/admin-expenses"
import { AdminOpsPage } from "@/pages/admin-ops"
import { AdminPage } from "@/pages/admin"
import { AdminSpendPage } from "@/pages/admin-spend"
import { AdminSttPage } from "@/pages/admin-stt"
import { AdminAgentEditorPage } from "@/pages/admin-agent-editor"
import { AdminUserConnectionsPage } from "@/pages/admin-connections"
import { AdminUserDetailPage } from "@/pages/admin-user-detail"
import { AdminWorkspaceMembersPage } from "@/pages/admin-workspace-members"
import { AdminUsersPage } from "@/pages/admin-users"
import { AgentEditorPage } from "@/pages/agent-editor"
import { AgentsPage } from "@/pages/agents"
import { BillingPage } from "@/pages/billing"
import { ChatPage } from "@/pages/chat"
import { ConnectionsPage } from "@/pages/connections"
import { LandingPage } from "@/pages/landing"
import { LoginPage } from "@/pages/login"
import { GoogleOAuthCallbackPage } from "@/pages/oauth-callback"
import { GuidePage } from "@/pages/guide"
import { PluginsPage } from "@/pages/plugins"
import { PrivacyPage } from "@/pages/privacy"
import { ResetPasswordPage } from "@/pages/reset-password"
import { RunsPage } from "@/pages/runs"
import { SchedulesPage } from "@/pages/schedules"
import { WorkspaceSettingsPage } from "@/pages/workspace-settings"

function CenteredLoading() {
  const t = useT()
  return (
    <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
      {t("common.loading")}
    </div>
  )
}

function Protected({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) {
    return <CenteredLoading />
  }
  return user ? <>{children}</> : <Navigate to="/login" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) {
    return <CenteredLoading />
  }
  if (!user) return <Navigate to="/login" replace />
  return user.is_admin ? <>{children}</> : <Navigate to="/chat" replace />
}

export default function App() {
  return (
    <ThemeProvider>
      <LocaleProvider>
        <AuthProvider>
          <ErrorBoundary>
          <Router>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/ru"
                element={
                  <LocaleProvider locale="ru">
                    <LandingPage />
                  </LocaleProvider>
                }
              />
              <Route
                path="/uz"
                element={
                  <LocaleProvider locale="uz">
                    <LandingPage />
                  </LocaleProvider>
                }
              />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Public (reachable while signed out): AcceptInvitePage itself detours
                  an unauthenticated visitor through /login and back. */}
              <Route path="/invite/:token" element={<AcceptInvitePage />} />
              <Route
                element={
                  <Protected>
                    <Layout />
                  </Protected>
                }
              >
                <Route path="/guide" element={<GuidePage />} />
              <Route path="/chat" element={<ChatPage />} />
                <Route path="/chat/:conversationId" element={<ChatPage />} />
                <Route path="/agents" element={<AgentsPage />} />
                <Route path="/agents/new" element={<AgentEditorPage />} />
                <Route path="/agents/:agentId" element={<AgentEditorPage />} />
                <Route path="/connections" element={<ConnectionsPage />} />
                <Route path="/plugins" element={<PluginsPage />} />
                <Route path="/schedules" element={<SchedulesPage />} />
                <Route path="/runs" element={<RunsPage />} />
                <Route path="/runs/:runId" element={<RunsPage />} />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/workspace" element={<WorkspaceSettingsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/oauth/google/callback" element={<GoogleOAuthCallbackPage />} />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <AdminRoute>
                      <AdminUsersPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/ops"
                  element={
                    <AdminRoute>
                      <AdminOpsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/spend"
                  element={
                    <AdminRoute>
                      <AdminSpendPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/stt"
                  element={
                    <AdminRoute>
                      <AdminSttPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/expenses"
                  element={
                    <AdminRoute>
                      <AdminExpensesPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/debts"
                  element={
                    <AdminRoute>
                      <AdminDebtsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId"
                  element={
                    <AdminRoute>
                      <AdminUserDetailPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId/agents/new"
                  element={
                    <AdminRoute>
                      <AdminAgentEditorPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId/agents/:agentId"
                  element={
                    <AdminRoute>
                      <AdminAgentEditorPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/users/:userId/connections"
                  element={
                    <AdminRoute>
                      <AdminUserConnectionsPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/workspaces/:workspaceId/members"
                  element={
                    <AdminRoute>
                      <AdminWorkspaceMembersPage />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <AdminRoute>
                      <AdminAuditPage />
                    </AdminRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/chat" replace />} />
              </Route>
            </Routes>
          </Router>
          </ErrorBoundary>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </LocaleProvider>
    </ThemeProvider>
  )
}
